import { type JSX, Show, onMount } from 'solid-js';
import { ViewThumbnail } from '../ViewThumbnail/ViewThumbnail';
import { IFeatureConfig } from '../../types/issue.types';
import type { MayaReservedViewData } from '@trimble/tc-issue-management-providers';
import { translate } from '@fulcrum/lib';
import { normalizeToFullIdentity } from '../../utils/issue-permissions.util';

const OPEN_IN_360_VIEWER_KEY = 'TC.Topic.Open360Viewer';
const getOpenIn360ViewerLabel = (): string => translate(OPEN_IN_360_VIEWER_KEY);

export interface IViewpoint {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- view_id is from API response format
  view_id: string;
  guid: string;
  name: string;
  thumbnail?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- created_at is from API response format
  created_at: string;
  author: string;
  /** Creator tiduuid — used for own-view delete when ACL is enabled. */
  createdByTiduuid?: string;
}

export interface IUser {
  tiduuid: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

interface IProps {
  onClose(): void;
  viewpointList: IViewpoint[];
  previewIndex: number;
  viewRedirection(attachmentId: string, type: string): void;
  previewMode: 'VIEW' | 'SLIDEVIEW';
  projectId: string;
  issueId: string;
  embedIn3D: boolean;
  deleteViewpoint(viewpointId: string): Promise<void>;
  setViewpointIndex(index: number): void;
  accessToken?: string;
  featureConfig?: IFeatureConfig;
  reservedViewDataMap?: Record<string, MayaReservedViewData>;
  open360Viewer?(viewId: string): boolean | Promise<boolean>;
  /** When ACL is enabled, delete requires issue update permission or view ownership. */
  hasIssueUpdatePermission?: boolean;
  currentUserTiduuid?: string;
  enableIssueAcl?: boolean;
}

export function isDeleteViewAllowedForAcl(
  enableIssueAcl: boolean,
  hasIssueUpdatePermission: boolean,
  isOwnView: boolean
): boolean {
  return !enableIssueAcl || hasIssueUpdatePermission || isOwnView;
}

export function isOwnIssueViewForUser(
  createdByTiduuid: string | undefined,
  currentUserTiduuid: string | undefined
): boolean {
  const createdBy = normalizeToFullIdentity(createdByTiduuid);
  const currentUser = normalizeToFullIdentity(currentUserTiduuid);
  return Boolean(createdBy && currentUser && createdBy === currentUser);
}

export function RightPanelOverlay(props: Readonly<IProps>): JSX.Element {
  onMount(() => {
    // Component is now focused only on viewpoint previews
  });

  const selectViewpoint = async (): Promise<void> => {
    // TODO: Implement viewpoint selection logic
    console.log('Select viewpoint clicked');
  };

  const selectedViewpoint = (): IViewpoint | undefined =>
    props.viewpointList[props.previewIndex];

  const selectedViewpointId = (): string | undefined =>
    selectedViewpoint()?.view_id;

  const onDeleteViewpoint = async (): Promise<void> => {
    const viewpoint = selectedViewpoint();
    if (!viewpoint) {
      props.onClose();
      return;
    }

    try {
      await props.deleteViewpoint(viewpoint.guid);
    } catch (e) {
      console.error(e);
    }
    props.onClose();
  };
  const isOwnView = (): boolean =>
    isOwnIssueViewForUser(
      selectedViewpoint()?.createdByTiduuid,
      props.currentUserTiduuid
    );
  const isDeleteViewAllowed = (): boolean =>
    isDeleteViewAllowedForAcl(
      props.enableIssueAcl ?? false,
      props.hasIssueUpdatePermission ?? true,
      isOwnView()
    );
  const makeViewpoint = (): string | undefined =>
    selectedViewpoint()?.thumbnail;
  const hasMayaReservedViewData = (): boolean => {
    const viewId = selectedViewpointId();
    return Boolean(viewId && props.reservedViewDataMap?.[viewId]);
  };
  const shouldShow360ViewerLabel = (): boolean =>
    hasMayaReservedViewData() &&
    props.featureConfig?.mayaReservedViewRouting?.redirectToProjectSight !==
      false;
  const onViewerButtonClick = async (): Promise<void> => {
    const viewId = selectedViewpointId();
    if (!viewId) {
      return;
    }

    if (hasMayaReservedViewData() && (await props.open360Viewer?.(viewId))) {
      return;
    }

    if (props.embedIn3D) {
      await selectViewpoint();
      return;
    }

    props.viewRedirection(viewId, 'VIEW');
  };

  return (
    <div class="topics-overlay z-modal-backdrop">
      <header>
        {props.previewMode !== 'SLIDEVIEW' && (
          <div class="panel-title">
            <div>
              <button class="button secondary" onClick={onViewerButtonClick}>
                {shouldShow360ViewerLabel()
                  ? getOpenIn360ViewerLabel()
                  : (props.featureConfig?.customText?.viewIn3DViewerLabel ??
                    translate('TC.FileViewer.Worldviewer'))}
              </button>
            </div>
            <div class="row-distribute-from-end">
              <button
                class="button white hollow"
                onClick={(): void => props.onClose()}
              >
                <i class="icon-font tc-icon-content-close"></i>
              </button>
            </div>
          </div>
        )}
      </header>
      <div class="moduleFluid px-2">
        <div class="topic-thumbnail-large">
          <ViewThumbnail
            view={{
              id: selectedViewpoint()?.guid || '',
              name: selectedViewpoint()?.name || '',
              thumbnail: makeViewpoint(),
              createdOn: selectedViewpoint()?.created_at || '',
              createdBy: selectedViewpoint()?.author
                ? {
                    firstName: selectedViewpoint()?.author.split(' ')[0] || '',
                    lastName: selectedViewpoint()?.author.split(' ')[1] || '',
                  }
                : undefined,
            }}
            class="topic-thumbnail-large"
            accessToken={props.accessToken}
          />
        </div>
        <div class="row-distribute-from-end">
          <div class="d-flex">
            <Show
              when={isDeleteViewAllowed()}
              fallback={
                <tc-custom-tooltip
                  text={translate('TC.Common.PermissionDeniedWarning')}
                  position="bottom"
                  arrow={false}
                  notDynamic={true}
                >
                  {/* @ts-expect-error -- Custom web component not recognized by TypeScript */}
                  <button class="button white hollow small" disabled={true}>
                    {translate('TC.Common.Delete')}
                  </button>
                </tc-custom-tooltip>
              }
            >
              <button
                class="button white hollow small"
                onClick={(): Promise<void> => onDeleteViewpoint()}
              >
                {translate('TC.Common.Delete')}
              </button>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
}
